import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import BrandingProvider from "@/components/BrandingProvider";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { lazy, Suspense } from "react";
import ThemeScope from "@/components/ThemeScope";

// Retry wrapper for lazy imports to handle transient chunk loading failures
function lazyRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>,
  retries = 2
): React.LazyExoticComponent<T["default"]> {
  return lazy(() =>
    factory().catch((err) => {
      if (retries > 0) {
        return new Promise<T>((resolve) => setTimeout(resolve, 1000)).then(() =>
          lazyRetry(factory, retries - 1) as any
        );
      }
      // Force reload on final failure (stale chunks after deploy)
      window.location.reload();
      return factory();
    })
  );
}

// Eagerly loaded pages (landing & critical public routes)
import NotFound from "./pages/NotFound";

// Marketing Layout (route-based i18n)
const MarketingLayout = lazyRetry(() => import("./components/layouts/MarketingLayout"));

// Marketing pages (under /:lang)
const LandingPage = lazyRetry(() => import("./pages/LandingPage"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const Features = lazyRetry(() => import("./pages/Features"));
const Changelog = lazyRetry(() => import("./pages/Changelog"));
const Blog = lazyRetry(() => import("./pages/Blog"));
const BlogPost = lazyRetry(() => import("./pages/BlogPost"));
const LegalPage = lazyRetry(() => import("./pages/LegalPage"));
const HelpCenter = lazyRetry(() => import("./pages/HelpCenter"));
const HelpArticlePage = lazyRetry(() => import("./pages/HelpArticle"));

// Non-marketing lazy pages
const Index = lazyRetry(() => import("./pages/Index"));
const Login = lazyRetry(() => import("./pages/Login"));
const Register = lazyRetry(() => import("./pages/Register"));
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const PublicMenu = lazyRetry(() => import("./pages/PublicMenu"));
const OrderTracking = lazyRetry(() => import("./pages/OrderTracking"));
const Unauthorized = lazyRetry(() => import("./pages/Unauthorized"));
const PaymentCallback = lazyRetry(() => import("./pages/PaymentCallback"));
const Profile = lazyRetry(() => import("./pages/Profile"));
const VerifyEmail = lazyRetry(() => import("./pages/VerifyEmail"));
const SitemapRedirect = lazyRetry(() => import("./pages/SitemapRedirect"));

// Lazy-loaded Guards & Layouts
const PlatformGuard = lazyRetry(() => import("./components/guards/PlatformGuard"));
const RestaurantGuard = lazyRetry(() => import("./components/guards/RestaurantGuard"));
const ShowcaseGuard = lazyRetry(() => import("./components/guards/ShowcaseGuard"));
const PlatformLayout = lazyRetry(() => import("./components/layouts/PlatformLayout"));
const RestaurantLayout = lazyRetry(() => import("./components/layouts/RestaurantLayout"));

// Lazy-loaded Platform Pages
const PlatformOverview = lazyRetry(() => import("./pages/platform/PlatformOverview"));
const PlatformBranding = lazyRetry(() => import("./pages/platform/PlatformBranding"));
const PlatformLanding = lazyRetry(() => import("./pages/platform/PlatformLanding"));
const PlatformPlans = lazyRetry(() => import("./pages/platform/PlatformPlans"));
const PlatformPaymentSettings = lazyRetry(() => import("./pages/platform/PlatformPaymentSettings"));
const PlatformInvoiceTemplate = lazyRetry(() => import("./pages/platform/PlatformInvoiceTemplate"));
const PlatformEmailSettings = lazyRetry(() => import("./pages/platform/PlatformEmailSettings"));
const PlatformChangelog = lazyRetry(() => import("./pages/platform/PlatformChangelog"));
const PlatformLegalPages = lazyRetry(() => import("./pages/platform/PlatformLegalPages"));
const PlatformRestaurants = lazyRetry(() => import("./pages/platform/PlatformRestaurants"));
const PlatformUsers = lazyRetry(() => import("./pages/platform/PlatformUsers"));
const PlatformPricingPage = lazyRetry(() => import("./pages/platform/PlatformPricingPage"));
const PlatformSystemHealth = lazyRetry(() => import("./pages/platform/PlatformSystemHealth"));
const PlatformFooterSettings = lazyRetry(() => import("./pages/platform/PlatformFooterSettings"));
const PlatformTracking = lazyRetry(() => import("./pages/platform/PlatformTracking"));
const PlatformBlog = lazyRetry(() => import("./pages/platform/PlatformBlog"));
const PlatformFeatures = lazyRetry(() => import("./pages/platform/PlatformFeatures"));
const PlatformWhatsApp = lazyRetry(() => import("./pages/platform/PlatformWhatsApp"));
const PlatformHelpCenter = lazyRetry(() => import("./pages/platform/PlatformHelpCenter"));
const PlatformStorage = lazyRetry(() => import("./pages/platform/PlatformStorage"));
const PlatformAuditLog = lazyRetry(() => import("./pages/platform/PlatformAuditLog"));

// Lazy-loaded Restaurant Pages
const RestaurantOverview = lazyRetry(() => import("./pages/restaurant/RestaurantOverview"));
const RestaurantInfo = lazyRetry(() => import("./pages/restaurant/RestaurantInfo"));
const RestaurantOnboarding = lazyRetry(() => import("./pages/restaurant/RestaurantOnboarding"));
const RestaurantMenu = lazyRetry(() => import("./pages/restaurant/RestaurantMenu"));

const RestaurantOrders = lazyRetry(() => import("./pages/restaurant/RestaurantOrders"));
const RestaurantDelivery = lazyRetry(() => import("./pages/restaurant/RestaurantDelivery"));
const RestaurantWaiters = lazyRetry(() => import("./pages/restaurant/RestaurantWaiters"));
const RestaurantStaff = lazyRetry(() => import("./pages/restaurant/RestaurantStaff"));
const RestaurantTheme = lazyRetry(() => import("./pages/restaurant/RestaurantTheme"));
const RestaurantSubscription = lazyRetry(() => import("./pages/restaurant/RestaurantSubscription"));
const RestaurantSettings = lazyRetry(() => import("./pages/restaurant/RestaurantSettings"));
const RestaurantQRCode = lazyRetry(() => import("./pages/restaurant/RestaurantQRCode"));
const RestaurantTrash = lazyRetry(() => import("./pages/restaurant/RestaurantTrash"));
const RestaurantStatus = lazyRetry(() => import("./pages/restaurant/RestaurantStatus"));
const RestaurantReports = lazyRetry(() => import("./pages/restaurant/RestaurantReports"));
const RestaurantBranches = lazyRetry(() => import("./pages/restaurant/RestaurantBranches"));
const RestaurantMenuExtract = lazyRetry(() => import("./pages/restaurant/RestaurantMenuExtract"));
const RestaurantCashier = lazyRetry(() => import("./pages/restaurant/RestaurantCashier"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" storageKey="platform-theme" disableTransitionOnChange>
    <LanguageProvider>
    <BrandingProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
        <ThemeScope />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" /></div>}>
        <Routes>
          {/* Root: redirect to /ar or login flow */}
          <Route path="/" element={<Index />} />

          {/* Marketing pages with route-based i18n: /:lang/... */}
          <Route path="/:lang" element={<MarketingLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="features" element={<Features />} />
            <Route path="changelog" element={<Changelog />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogPost />} />
            <Route path="legal/:slug" element={<LegalPage />} />
            <Route path="help" element={<HelpCenter />} />
            <Route path="help/:slug" element={<HelpArticlePage />} />
          </Route>

          {/* Legacy marketing routes: redirect to /ar/... */}
          <Route path="/pricing" element={<Navigate to="/ar/pricing" replace />} />
          <Route path="/features" element={<Navigate to="/ar/features" replace />} />
          <Route path="/changelog" element={<Navigate to="/ar/changelog" replace />} />
          <Route path="/blog" element={<Navigate to="/ar/blog" replace />} />
          <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
          <Route path="/legal/:slug" element={<LegacyLegalRedirect />} />

          {/* Non-marketing public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/menu/:slug" element={<PublicMenu />} />
          <Route path="/menu/:slug/:branchSlug" element={<PublicMenu />} />
          <Route path="/menu/:slug/:branchSlug/w/:waiterName" element={<PublicMenu />} />
          <Route path="/track/:trackingCode" element={<OrderTracking />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/payment/callback" element={<PaymentCallback />} />
          <Route path="/sitemap.xml" element={<SitemapRedirect />} />

          {/* Platform Routes (Super Admin Only) */}
          <Route
            path="/platform"
            element={
              <PlatformGuard>
                <PlatformLayout />
              </PlatformGuard>
            }
          >
            <Route index element={<PlatformOverview />} />
            <Route path="branding" element={<PlatformBranding />} />
            <Route path="design" element={<PlatformBranding />} />
            <Route path="landing" element={<PlatformLanding />} />
            <Route path="footer" element={<PlatformFooterSettings />} />
            <Route path="plans" element={<PlatformPlans />} />
            <Route path="pricing-page" element={<PlatformPricingPage />} />
            <Route path="payments" element={<PlatformPaymentSettings />} />
            <Route path="invoice-template" element={<PlatformInvoiceTemplate />} />
            <Route path="email" element={<PlatformEmailSettings />} />
            <Route path="changelog" element={<PlatformChangelog />} />
            <Route path="legal-pages" element={<PlatformLegalPages />} />
            <Route path="restaurants" element={<PlatformRestaurants />} />
            <Route path="users" element={<PlatformUsers />} />
            <Route path="tracking" element={<PlatformTracking />} />
            <Route path="blog" element={<PlatformBlog />} />
            <Route path="features" element={<PlatformFeatures />} />
            <Route path="system-health" element={<PlatformSystemHealth />} />
            <Route path="whatsapp" element={<PlatformWhatsApp />} />
            <Route path="storage" element={<PlatformStorage />} />
            <Route path="help-center" element={<PlatformHelpCenter />} />
            <Route path="audit-log" element={<PlatformAuditLog />} />
          </Route>

          {/* Restaurant Routes (Restaurant Admin Only) */}
          <Route
            path="/restaurant"
            element={
              <RestaurantGuard>
                <RestaurantLayout />
              </RestaurantGuard>
            }
          >
            <Route index element={<RestaurantOverview />} />
            <Route path="info" element={<RestaurantInfo />} />
            <Route path="menu" element={<RestaurantMenu />} />
            <Route path="categories" element={<RestaurantMenu />} />
            <Route path="products" element={<RestaurantMenu />} />
            
            <Route path="orders" element={<RestaurantOrders />} />
            <Route path="delivery" element={<RestaurantDelivery />} />
            <Route path="tables" element={<RestaurantWaiters />} />
            <Route path="waiters" element={<RestaurantWaiters />} />
            <Route path="staff" element={<RestaurantStaff />} />
            <Route path="theme" element={<RestaurantTheme />} />
            <Route path="subscription" element={<RestaurantSubscription />} />
            <Route path="settings" element={<RestaurantSettings />} />
            <Route path="status" element={<RestaurantStatus />} />
            <Route path="qr-code" element={<RestaurantQRCode />} />
            <Route path="trash" element={<RestaurantTrash />} />
            <Route path="reports" element={<RestaurantReports />} />
            <Route path="branches" element={<RestaurantBranches />} />
            <Route path="extract-menu" element={<RestaurantMenuExtract />} />
          </Route>

          {/* Onboarding & Cashier (outside layout for full screen) */}
          <Route
            path="/restaurant/onboarding"
            element={
              <RestaurantGuard>
                <RestaurantOnboarding />
              </RestaurantGuard>
            }
          />
          <Route
            path="/restaurant/cashier"
            element={
              <RestaurantGuard>
                <RestaurantCashier />
              </RestaurantGuard>
            }
          />

          {/* Showcase Mode — uses real RestaurantLayout with static data */}
          <Route
            path="/restaurant/showcase"
            element={
              <ShowcaseGuard>
                <RestaurantLayout />
              </ShowcaseGuard>
            }
          >
            <Route index element={<RestaurantOverview />} />
            <Route path="info" element={<RestaurantInfo />} />
            <Route path="menu" element={<RestaurantMenu />} />
            <Route path="categories" element={<RestaurantMenu />} />
            <Route path="products" element={<RestaurantMenu />} />
            <Route path="orders" element={<RestaurantOrders />} />
            <Route path="delivery" element={<RestaurantDelivery />} />
            <Route path="waiters" element={<RestaurantWaiters />} />
            <Route path="staff" element={<RestaurantStaff />} />
            <Route path="theme" element={<RestaurantTheme />} />
            <Route path="settings" element={<RestaurantSettings />} />
            <Route path="status" element={<RestaurantStatus />} />
            <Route path="qr-code" element={<RestaurantQRCode />} />
            <Route path="trash" element={<RestaurantTrash />} />
            <Route path="reports" element={<RestaurantReports />} />
            <Route path="branches" element={<RestaurantBranches />} />
            <Route path="extract-menu" element={<RestaurantMenuExtract />} />
            <Route path="cashier" element={<RestaurantCashier />} />
          </Route>

          {/* Legacy redirects */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/restaurant/:id" element={<Navigate to="/" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </BrandingProvider>
    </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

// Helper components for legacy redirects
const LegacyBlogRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/ar/blog/${slug}`} replace />;
};

const LegacyLegalRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/ar/legal/${slug}`} replace />;
};




export default App;
